import type { ColumnDef, SettlementInstructionDirectionEnum } from '@talos/kyoko';
import type { FieldDefinition } from '../../../../utils/fieldDefinitionUtils';
import { getColumnTitle, getColumnTooltip } from '../../../../utils/fieldDefinitionUtils';

/**
 * Empty value/currency object for size columns when no value should be displayed
 */
export const EMPTY_SIZE_VALUE = {
  value: '',
  currency: '',
} as const;

/**
 * Base type for data rows used in directional amount columns
 */
interface DirectionalAmountData {
  Direction?: string;
  Amount?: string;
  Asset?: string;
}

interface DirectionalAmountColumnOptions {
  id: string;
  fieldDefinition: FieldDefinition;
  direction: SettlementInstructionDirectionEnum;
  initialWidth?: number;
}

/**
 * Creates a size column that shows an amount only when the row's direction matches the specified direction.
 * Used for Outgoing/Incoming columns in LP Settlement grids.
 */
export function createDirectionalAmountColumn(
  options: DirectionalAmountColumnOptions
): ColumnDef<DirectionalAmountData> {
  const { id, fieldDefinition, direction, initialWidth = 120 } = options;

  return {
    id,
    field: 'Amount',
    type: 'size',
    title: getColumnTitle(fieldDefinition),
    description: getColumnTooltip(fieldDefinition),
    initialWidth,
    enableRowGroup: false,
    params: {
      currencyField: 'Asset',
      showInTermsOfContracts: true,
      highlightNegative: true,
      transformValue: (value, { data }) => (data?.Direction === direction ? value : EMPTY_SIZE_VALUE),
    },
  };
}
